import { StateMachine } from "@/core/arch/state_machine";
import type {
  JobHistoryRepository,
  JobHistoryRow,
} from "./job_history_repository";

export interface ToolStatusState {
  /** Most recent run per job type. A key present with null means "never run". */
  latest: Record<string, JobHistoryRow | null>;
  status: "idle" | "loading" | "loaded";
}

/**
 * Loads the most recent run of each job-backed tool for the tools table.
 *
 * A failed lookup resolves to "never run" rather than an error state: the table
 * is a launcher first, and one unreadable status column must not hide the rest
 * of the tools.
 */
export class ToolStatusBloc extends StateMachine<ToolStatusState> {
  constructor(
    private readonly repo: JobHistoryRepository,
    private readonly jobTypes: string[],
  ) {
    super({ latest: {}, status: "idle" });
  }

  async load(): Promise<void> {
    this.setState({ latest: {}, status: "loading" });

    const entries = await Promise.all(
      this.jobTypes.map(async (jobType) => {
        try {
          return [jobType, await this.repo.latestJob(jobType)] as const;
        } catch {
          return [jobType, null] as const;
        }
      }),
    );

    this.setState({ latest: Object.fromEntries(entries), status: "loaded" });
  }
}
